import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Box,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import {
  DiagnosisEnergyUseReadDTO,
  DiagnosisEnergyUseCreateDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { ActionButton } from '../../common';

interface EditEnergyUseDialogProps {
  open: boolean;
  onClose: () => void;
  energyUse: DiagnosisEnergyUseReadDTO | null;
  diagnosisId: string;
}

export const EditEnergyUseDialog: React.FC<EditEnergyUseDialogProps> = ({
  open,
  onClose,
  energyUse,
  diagnosisId,
}) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<DiagnosisEnergyUseCreateDTO>({
    defaultValues: energyUse
      ? {
          diagnosis_id: diagnosisId,
          code: energyUse.code ?? undefined,
          description: energyUse.description ?? undefined,
          original_energy_use_id: energyUse.original_energy_use_id ?? undefined,
        }
      : {
          diagnosis_id: diagnosisId,
        },
  });

  React.useEffect(() => {
    if (open) {
      if (energyUse) {
        reset({
          diagnosis_id: diagnosisId,
          code: energyUse.code ?? undefined,
          description: energyUse.description ?? undefined,
          original_energy_use_id: energyUse.original_energy_use_id ?? undefined,
        });
      } else {
        reset({
          diagnosis_id: diagnosisId,
        });
      }
    }
  }, [open, energyUse, diagnosisId, reset]);

  const createMutation = useMutation(
    async (data: DiagnosisEnergyUseCreateDTO) => {
      return await apiClient.create_item_diagnosis_energy_use__post(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-energy-use-${diagnosisId}`);
        toast.success('Uso energetico creato con successo');
        onClose();
      },
      onError: (error: any) => {
        if (Array.isArray(error?.detail)) {
          for (const detail of error.detail) {
            toast.error(`${detail.msg} (${detail.loc[1]})`);
          }
        } else {
          toast.error("Creazione dell'uso energetico non riuscita");
        }
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }: { id: string; data: DiagnosisEnergyUseCreateDTO }) => {
      return await apiClient.update_item_diagnosis_energy_use__item_id__put(id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-energy-use-${diagnosisId}`);
        toast.success('Uso energetico aggiornato con successo');
        onClose();
      },
      onError: (error: any) => {
        if (Array.isArray(error?.detail)) {
          for (const detail of error.detail) {
            toast.error(`${detail.msg} (${detail.loc[1]})`);
          }
        } else {
          toast.error("Aggiornamento dell'uso energetico non riuscito");
        }
      },
    }
  );

  const onSubmit = (data: DiagnosisEnergyUseCreateDTO) => {
    if (energyUse) {
      updateMutation.mutate({ id: energyUse.id!, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      reset({
        diagnosis_id: diagnosisId,
        code: undefined,
        description: undefined,
        original_energy_use_id: undefined,
      });
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{energyUse ? 'Modifica Uso Energetico' : 'Nuovo Uso Energetico'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Codice"
                {...register('code', { required: 'Il codice è obbligatorio' })}
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Descrizione"
                {...register('description')}
                error={!!errors.description}
                helperText={errors.description?.message}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip="Salva"
              onClick={handleSubmit(onSubmit)}
              variant="filled"
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
